import React from "react";
import { Cube, CheckCircle } from "@phosphor-icons/react";

/**
 * Compact chip showing specialty item progress on an order.
 * Renders nothing if has_specialty_items is falsy.
 *
 * Backend fields used (order level):
 *   has_specialty_items: boolean
 *   specialty_count: number
 *   specialty_received_count: number
 */
export default function SpecialtyChip({ order, size = "sm" }) {
  if (!order?.has_specialty_items) return null;

  const total = order.specialty_count || 0;
  const received = order.specialty_received_count || 0;
  const allReceived = total > 0 && received >= total;

  const px = size === "md" ? "px-2.5" : "px-2";
  const py = size === "md" ? "py-1" : "py-0.5";
  const txt = size === "md" ? "text-[11px]" : "text-[10px]";

  const styles = allReceived
    ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
    : "bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800";

  return (
    <span
      data-testid="specialty-chip"
      className={`inline-flex items-center gap-1 rounded border font-semibold uppercase tracking-[0.15em] ${px} ${py} ${txt} whitespace-nowrap ${styles}`}
      title={allReceived ? "All specialty items received" : "Pending vendor delivery"}
    >
      {allReceived
        ? <CheckCircle size={11} weight="bold" />
        : <Cube size={11} weight="bold" />}
      Specialty {received}/{total}
    </span>
  );
}

export function SpecialtyPaymentChip({ order, size = "sm" }) {
  const status = order?.specialty_payment_status;
  if (!status || typeof status !== "string") return null;

  const px = size === "md" ? "px-2.5" : "px-2";
  const py = size === "md" ? "py-1" : "py-0.5";
  const txt = size === "md" ? "text-[11px]" : "text-[10px]";

  // Friendly label
  const label = status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <span
      data-testid="specialty-payment-chip"
      className={`inline-flex items-center rounded border font-semibold ${px} ${py} ${txt} whitespace-nowrap bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700`}
      title={`Payment status: ${status}`}
    >
      $ {label}
    </span>
  );
}
